import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Mail, Building, Calendar, Shield, Trash2, AlertCircle } from 'lucide-react';
import { useUserStore } from '../store/useUserStore';
import { useTenantStore } from '../store/useTenantStore';
import { useAuthStore } from '../store/useAuthStore';
import { apiClient } from '../lib/api/client';

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const user = useUserStore((state) => state.user);
  const tenantId = useTenantStore((state) => state.tenantId);
  const formattedCode = useTenantStore((state) => state.formattedCode);
  const clearAuth = useAuthStore((state) => state.clearAuth);
  const clearUserInfo = useUserStore((state) => state.clearUserInfo);
  const clearTenant = useTenantStore((state) => state.clearTenant);
  
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Enable scrolling for this page
  React.useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    const rootElement = document.getElementById('root');
    const originalRootOverflow = rootElement?.style.overflow || '';
    
    document.body.style.overflow = 'auto';
    if (rootElement) {
      rootElement.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = originalOverflow;
      if (rootElement) {
        rootElement.style.overflow = originalRootOverflow;
      }
    };
  }, []);

  const handleLogout = () => {
    clearAuth();
    clearUserInfo();
    navigate('/');
  };

  const handleDeleteRequest = async () => {
    if (!deleteReason.trim()) {
      setDeleteError('Lütfen hesap silme nedeninizi belirtin.');
      return;
    }

    setIsSubmitting(true);
    setDeleteError(null);

    try {
      // API endpoint for account deletion request
      await apiClient.post('/services/app/Account/RequestAccountDeletion', {
        reason: deleteReason,
        userEmail: user?.emailAddress,
        userId: user?.id,
        tenantId: tenantId
      });

      setDeleteSuccess(true);
      setShowDeleteModal(false);
      
      // Show success message and logout after 3 seconds
      setTimeout(() => {
        handleLogout();
      }, 3000);
    } catch (error: any) {
      console.error('Account deletion request failed:', error);
      setDeleteError(error.response?.data?.error?.message || 'Hesap silme talebi gönderilemedi. Lütfen daha sonra tekrar deneyin.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Kullanıcı bilgileri yükleniyor...</p>
          <Link to="/" className="text-blue-600 hover:text-blue-700">
            Ana Sayfaya Dön
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8 pb-16">
        <Link to="/library" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Kütüphaneye Dön
        </Link>
        
        <div className="bg-white rounded-lg shadow-md">
          {/* Profile Header */}
          <div className="border-b p-6">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                <User className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{user.name} {user.surname}</h1>
                <p className="text-gray-600">{user.userName}</p>
              </div>
            </div>
          </div>

          {/* Profile Information */}
          <div className="p-6 space-y-6">
            {/* User Information */}
            <section>
              <h2 className="text-lg font-semibold mb-4 text-gray-900">Kullanıcı Bilgileri</h2>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">E-posta</p>
                    <p className="text-gray-900">{user.emailAddress}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <Building className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Kurum</p>
                    <p className="text-gray-900">{formattedCode?.toUpperCase() || 'Belirtilmemiş'}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <Calendar className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Kayıt Tarihi</p>
                    <p className="text-gray-900">
                      {user.creationTime ? new Date(user.creationTime).toLocaleDateString('tr-TR') : 'Belirtilmemiş'}
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Privacy & Security */}
            <section>
              <h2 className="text-lg font-semibold mb-4 text-gray-900">Gizlilik ve Güvenlik</h2>
              <div className="space-y-3">
                <Link 
                  to="/privacy-policy" 
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <Shield className="w-5 h-5 text-gray-400" />
                    <span className="text-gray-700">Gizlilik Politikası</span>
                  </div>
                  <span className="text-gray-400">→</span>
                </Link>
                
                <Link 
                  to="/terms-of-service" 
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <Shield className="w-5 h-5 text-gray-400" />
                    <span className="text-gray-700">Kullanım Koşulları</span>
                  </div>
                  <span className="text-gray-400">→</span>
                </Link>
              </div>
            </section>

            {/* Account Actions */}
            <section className="border-t pt-6">
              <h2 className="text-lg font-semibold mb-4 text-gray-900">Hesap İşlemleri</h2>
              <div className="space-y-3">
                <button
                  onClick={handleLogout}
                  className="w-full text-left p-3 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-between"
                >
                  <span className="text-gray-700">Çıkış Yap</span>
                  <span className="text-gray-400">→</span>
                </button>
                
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="w-full text-left p-3 rounded-lg hover:bg-red-50 transition-colors flex items-center justify-between text-red-600"
                >
                  <div className="flex items-center space-x-3">
                    <Trash2 className="w-5 h-5" />
                    <span>Hesabımı Sil</span>
                  </div>
                  <span>→</span>
                </button>
              </div>
            </section>
          </div>
        </div>

        {/* Success Message */}
        {deleteSuccess && (
          <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-green-600 mt-0.5" />
              <div className="text-sm text-green-800">
                <p className="font-semibold mb-1">Hesap silme talebiniz alındı</p>
                <p>Talebiniz en kısa sürede değerlendirilecektir. Hesabınız silinene kadar tüm verilerinize erişebilirsiniz.</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Hesap Silme Talebi</h3>
            
            <div className="mb-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-semibold mb-1">Önemli Uyarı</p>
                    <p>Hesabınızı sildiğinizde tüm verileriniz kalıcı olarak silinecektir. Bu işlem geri alınamaz.</p>
                  </div>
                </div>
              </div>
              
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Hesabınızı neden silmek istiyorsunuz?
              </label>
              <textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={4}
                placeholder="Lütfen nedeninizi belirtin..."
                required
              />
              {deleteError && (
                <p className="mt-2 text-sm text-red-600">{deleteError}</p>
              )}
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteReason('');
                  setDeleteError(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={isSubmitting}
              >
                İptal
              </button>
              <button
                onClick={handleDeleteRequest}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Gönderiliyor...' : 'Silme Talebi Gönder'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;